import { lbBrand } from '@fuse/colors';
import { TextFieldFormsy } from '@fuse/core/formsy';
import { makeStyles } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import { submitLogin } from 'app/auth/store/loginSlice';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import LoadingButton from 'app/main/design-system/components/LoadingButton';
import clsx from 'clsx';
import Formsy from 'formsy-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  textField: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: lbBrand.black,
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: lbBrand.red,
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: lbBrand.black,
    },
    '& .MuiInputLabel-outlined.Mui-error': {
      color: lbBrand.red,
    },
  },
}));

function JWTLoginTab() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const login = useSelector(({ auth }) => auth.login);

  const [isFormValid, setIsFormValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const formRef = useRef(null);

  function disableButton() {
    setIsFormValid(false);
  }

  function enableButton() {
    setIsFormValid(true);
  }

  function handleSubmit(model) {
    dispatch(submitLogin(model));
  }

  useEffect(() => {
    if (login.error && (login.error.email || login.error.password)) {
      formRef.current.updateInputsWithError({
        ...login.error,
      });
    }
  }, [login.error]);

  return (
    <div className="w-full">
      <Formsy
        onValidSubmit={handleSubmit}
        onValid={enableButton}
        onInvalid={disableButton}
        ref={formRef}
        className="flex flex-col justify-center w-full"
      >
        <TextFieldFormsy
          className={clsx('mb-16', classes.textField)}
          type="text"
          name="email"
          label={t('basic.fields.email')}
          validations={{
            minLength: 4,
          }}
          validationErrors={{
            minLength: 'Min character length is 4',
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Icon className="text-20" color="action">
                  email
                </Icon>
              </InputAdornment>
            ),
          }}
          variant="outlined"
          required
        />

        <TextFieldFormsy
          className={clsx('mb-16', classes.textField)}
          type="password"
          name="password"
          label={t('basic.fields.password')}
          validations={{
            minLength: 2,
          }}
          validationErrors={{
            minLength: 'Min character length is 2',
          }}
          InputProps={{
            className: 'pr-2',
            type: showPassword ? 'text' : 'password',
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  <Icon className="text-20" color="action">
                    {showPassword ? 'visibility' : 'visibility_off'}
                  </Icon>
                </IconButton>
              </InputAdornment>
            ),
          }}
          variant="outlined"
          required
        />

        <div className="flex items-center justify-end">
          <Typography
            className="font-medium cursor-pointer"
            component={Link}
            role="button"
            to={routesUrl.forgotPassword}
            style={{ color: lbBrand.black }}
          >
            <span className="mx-4">{t('pages.login.forgotPassword')} ?</span>
          </Typography>
        </div>

        <div className={clsx(classes.wrapper, 'mt-16')}>
          <LoadingButton
            isLoading={login.isLoading}
            loaderSize={16}
            type="submit"
            variant="contained"
            color="primary"
            className="w-full mx-auto normal-case"
            aria-label="LOG IN"
            disabled={!isFormValid || login.isLoading}
            value="legacy"
          >
            {t('pages.login.login')}
          </LoadingButton>
        </div>
      </Formsy>
    </div>
  );
}

export default JWTLoginTab;
